/**
 * Text-to-Speech Service for voice feedback
 */
class SpeechService {
  private synth: SpeechSynthesis | null = null;
  private enabled: boolean = true;

  constructor() {
    if ('speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
      console.log('🔊 Text-to-Speech initialized');
    } else {
      console.warn('⚠️ Text-to-Speech not supported');
    }
  }

  /**
   * Speak a message
   */
  speak(text: string, options?: { rate?: number; pitch?: number; volume?: number }) {
    if (!this.synth || !this.enabled) {
      console.log('🔇 Speech disabled or not supported');
      return;
    }

    // Cancel any ongoing speech
    this.synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = options?.rate || 1.0;
    utterance.pitch = options?.pitch || 1.0;
    utterance.volume = options?.volume || 1.0;
    utterance.lang = 'en-US';

    console.log('🔊 Speaking:', text);
    this.synth.speak(utterance);
  }

  /**
   * Stop current speech
   */
  stop() {
    if (this.synth) {
      this.synth.cancel();
    }
  }

  /**
   * Enable/disable speech
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      this.stop();
    }
  }

  /**
   * Check if speech is enabled
   */
  isEnabled(): boolean {
    return this.enabled && this.synth !== null;
  }

  /**
   * Announce search results
   */
  announceSearchResults(count: number, query: string) {
    if (count === 0) {
      this.speak(`No wells found matching ${query}`);
    } else if (count === 1) {
      this.speak(`Found 1 well matching ${query}`);
    } else {
      this.speak(`Found ${count} wells matching ${query}`);
    }
  }

  /**
   * Announce navigation
   */
  announceNavigation(wellName: string) {
    this.speak(`Navigating to ${wellName}`);
  }

  /**
   * Announce favorite added
   */
  announceFavoriteAdded(wellName: string) {
    this.speak(`Added ${wellName} to favorites`);
  }

  /**
   * Announce favorite removed
   */
  announceFavoriteRemoved(wellName: string) {
    this.speak(`Removed ${wellName} from favorites`);
  }

  /**
   * Announce offline mode
   */
  announceOfflineMode() {
    this.speak('Connection lost. Using offline mode.');
  }

  /**
   * Announce online mode
   */
  announceOnlineMode() {
    this.speak('Connection restored.');
  }

  /**
   * Announce listening status
   */
  announceListening() {
    // Short beep-like sound (brief "ready" message)
    this.speak('Ready', { rate: 1.5, pitch: 1.2 });
  }

  /**
   * Announce error
   */
  announceError(message: string) {
    this.speak(`Error: ${message}`);
  }
}

// Export singleton instance
export const speechService = new SpeechService();
